// Tenant picker for admin forms (docs/adr/2026-09-28-customer-tenancy.md):
// "Internal" (no tenant) or one customer tenant.
import { useMemo } from "react";

import { useListTenants, type Tenant } from "@/api/generated";
import { Field } from "@/components/common/common";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useIsAdmin } from "@/lib/lookups";

export const INTERNAL = "__internal__";

/** Tenants by id (admins only; empty for everyone else). */
export function useTenantMap(): Map<string, Tenant> {
  const q = useListTenants({ query: { enabled: useIsAdmin(), staleTime: 60_000 } });
  return useMemo(() => new Map((q.data ?? []).map((t) => [t.id, t])), [q.data]);
}

export function TenantSelect({ value, onChange, label = "Customer", hint }: {
  value: string; onChange: (v: string) => void; label?: string; hint?: string;
}) {
  const tenants = useTenantMap();
  return (
    <Field label={label} hint={hint ?? "Customers see only what is tagged with their tenant."}>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={INTERNAL}>Internal (no customer)</SelectItem>
          {[...tenants.values()].sort((a, b) => a.name.localeCompare(b.name)).map((t) => (
            <SelectItem key={t.id} value={t.id}><span className="truncate">{t.name}</span></SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  );
}

export function TenantBadge({ id }: { id?: string | null }) {
  const tenants = useTenantMap();
  if (!id) return null;
  return <Badge variant="outline" className="text-status-maintenance">{tenants.get(id)?.name ?? "customer"}</Badge>;
}

export const tenantValue = (id?: string | null) => id ?? INTERNAL;
export const tenantId = (v: string) => (v === INTERNAL ? null : v);
